import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';

// Common domain typos to detect accidental errors
const TYPO_PATTERNS: { pattern: RegExp, suggestion: string }[] = [
  { pattern: /gmail\.co$/i, suggestion: 'gmail.com' },
  { pattern: /gmail\.cm$/i, suggestion: 'gmail.com' },
  { pattern: /gmail\.con$/i, suggestion: 'gmail.com' },
  { pattern: /gmial\.com$/i, suggestion: 'gmail.com' },
  { pattern: /gmal\.com$/i, suggestion: 'gmail.com' },
  { pattern: /yahooo\.com$/i, suggestion: 'yahoo.com' },
  { pattern: /yaho\.com$/i, suggestion: 'yahoo.com' },
  { pattern: /yahoo\.co$/i, suggestion: 'yahoo.com' },
  { pattern: /hotmal\.com$/i, suggestion: 'hotmail.com' },
  { pattern: /hotmial\.com$/i, suggestion: 'hotmail.com' },
  { pattern: /hotmail\.co$/i, suggestion: 'hotmail.com' },
  { pattern: /outlok\.com$/i, suggestion: 'outlook.com' },
  { pattern: /outloo\.com$/i, suggestion: 'outlook.com' },
  { pattern: /outlook\.co$/i, suggestion: 'outlook.com' },
  { pattern: /icoud\.com$/i, suggestion: 'icloud.com' },
  { pattern: /icloud\.co$/i, suggestion: 'icloud.com' },
  { pattern: /aol\.co$/i, suggestion: 'aol.com' }
];

@Component({
  selector: 'app-hospital-group-email-dialog',
  templateUrl: './hospital-group-email-dialog.component.html',
  styleUrls: ['./hospital-group-email-dialog.component.scss'],
  standalone: false
})
export class HospitalGroupEmailDialogComponent {

  // Dialog states: 'initial' | 'contactInput' | 'confirm'
  currentState: string = 'initial';

  contactForm = new FormGroup({
    email: new FormControl('', [Validators.email]),
    phone: new FormControl('', [Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)])
  });
  domainError: string = '';
  contactError: string = '';
  enteredEmail: string = '';
  enteredPhone: string = '';

  constructor(
    public dialogRef: MatDialogRef<HospitalGroupEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data?.mode === 'edit') {
      this.currentState = 'contactInput';
      this.contactForm.setValue({
        email: data.email || '',
        phone: data.phone || ''
      });
    } else if (data?.startAt === 'contactInput') {
      // Opened from the post-login prompt's Yes redirect: skip the introduction
      this.currentState = 'contactInput';
    }
  }

  get emailControl(): FormControl { return this.contactForm.controls.email; }
  get phoneControl(): FormControl { return this.contactForm.controls.phone; }

  onYes(): void {
    // Post-login prompt: hand off to the profile Notifications settings tab
    if (this.data?.redirectOnYes) {
      this.dialogRef.close({ action: 'configure' });
      return;
    }
    this.currentState = 'contactInput';
    this.contactForm.reset({ email: '', phone: '' });
    this.domainError = '';
    this.contactError = '';
  }

  onNo(): void {
    this.dialogRef.close({ action: 'no' });
  }

  onSkip(): void {
    this.dialogRef.close({ action: 'skip' });
  }

  onSubmitContacts(): void {
    this.domainError = '';
    this.contactError = '';

    const email = (this.emailControl.value || '').trim().toLowerCase();
    const phone = (this.phoneControl.value || '').trim();

    this.emailControl.setValue(email, { emitEvent: false });
    this.phoneControl.setValue(phone, { emitEvent: false });

    if (!email && !phone && this.data?.mode !== 'edit') {
      this.contactError = 'Enter a hospital email, hospital phone, or both.';
      this.contactForm.markAllAsTouched();
      return;
    }

    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    // Check for domain typos
    const domain = email ? email.split('@')[1] : '';
    if (domain) {
      for (const { pattern } of TYPO_PATTERNS) {
        if (pattern.test(domain)) {
          this.domainError = 'Please input a valid email address.';
          return;
        }
      }
    }

    // Move to confirmation
    this.enteredEmail = email;
    this.enteredPhone = phone;
    this.currentState = 'confirm';
  }

  onConfirmYes(): void {
    this.dialogRef.close({ action: 'yes', email: this.enteredEmail, phone: this.enteredPhone });
  }

  onConfirmEdit(): void {
    this.currentState = 'contactInput';
    this.domainError = '';
  }

  onBackToInitial(): void {
    if (this.data?.mode === 'edit' || this.data?.startAt === 'contactInput') {
      this.dialogRef.close();
      return;
    }

    this.currentState = 'initial';
    this.contactForm.reset({ email: '', phone: '' });
    this.domainError = '';
    this.contactError = '';
  }
}
